import { useRef, useContext, useCallback, useEffect, useState } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { createUseStyles, useTheme } from 'react-jss'
import classNames from 'classnames'
import Logo from '@/components/Logo'
import DelayLink from '@/components/DelayLink'
import BtnLink from '@/components/BtnLink'
import SimpleSplitText from '@/components/SimpleSplitText'
import CartButton from '@/components/CartButton'
import { Context } from '@/context'
import { Context as ScrollbarContext } from '@/context/scrollbar'
import { useResize } from '@/components/Handlers'
import actions from '@/actions'
import style from './style'

const useStyles = createUseStyles(style)

const Header = ({
  menuLabel,
  closeLabel,
}) => {
  const theme = useTheme()
  const { isNavOpen, setNavOpen, setCollectionNavOpen, setHeaderRef, setHeaderHeight, isBackButtonVisible, backButtonLink, isProductPage } = useContext(Context)
  const classes = useStyles({ isIE11: theme.detect.isIE11 })

  const { mediaQueryKey, strings } = useSelector((state) => ({
    strings: state.language.string,
    mediaQueryKey: state.mediaQuery.key,
  }), shallowEqual)

  const dispatch = useDispatch()
  const setIsBack = useCallback((bool) => dispatch(actions.page.setIsBack(bool)), [dispatch])

  const history = useHistory()
  const $root = useRef()
  const rootBounds = useRef()
  const [isWhiteBackground] = useState(true)
  const [isMenuButtonHover, setMenuButtonHover] = useState(false)
  const { scrollbar } = useContext(ScrollbarContext)

  // share header ref to using it in <Nav/> for accessibility purpose
  useEffect(() => {
    setHeaderRef($root)
  }, [])

  const handleMouseCollection = (e) => {
    if (e.type === 'mouseenter') {
      if (mediaQueryKey > 2) setCollectionNavOpen(true)
    }
    if (e.type === 'mouseleave' && mediaQueryKey > 2) {
      if (mediaQueryKey > 2) setCollectionNavOpen(false)
    }
  }

  useResize(() => {
    if ($root.current) {
      rootBounds.current = $root.current.getBoundingClientRect()
      setHeaderHeight($root.current.getBoundingClientRect().height)
    }
  })

  const handleMainMenuClick = () => setNavOpen(!isNavOpen)

  const preventPressSpacebar = useCallback((node) => {
    if (node !== null) {
      node.addEventListener('keyup', (e) => {
        if (e.keyCode === 32 && e.code === 'Space') e.preventDefault()
      })
    }
  }, [])

  const handleBackButton = () => {
    setIsBack(true)
    if (!backButtonLink) history.goBack()
  }

  const renderBackButton = () => {
    return (
      <BtnLink
        className={classNames({
          [classes.menuButtonBackMobile]: mediaQueryKey <= 1,
          [classes.hide]: !isBackButtonVisible,
        })}
        link={backButtonLink}
        onClick={handleBackButton}
        label={strings.back_label}
        icon="back"
        btn={mediaQueryKey > 1}
        theme={mediaQueryKey <= 1 ? isProductPage ? 'dark' : 'light' : 'grey-light'} // eslint-disable-line no-nested-ternary
      />
    )
  }

  const renderCollectionButton = () => {
    return mediaQueryKey > 1 && (
      <DelayLink
        className={classNames({
          [classes.menuButton]: true,
          [classes.hide]: isNavOpen || isMenuButtonHover,
        })}
        to={strings.collections_menu_link}
        onMouseEnter={handleMouseCollection}
        onMouseLeave={handleMouseCollection}
      >
        <SimpleSplitText
          text={strings.collections_menu_label}
          animation="linear"
        />
      </DelayLink>
    )
  }

  const handleCartClick = () => {
    if (window.Ecwid) {
      window.Ecwid.Cart.gotoCheckout()
      scrollbar.destroy()
      window.Ecwid.OnPageSwitch.add((e) => {
        if (e.type === 'CATEGORY') scrollbar.init()
      })
      window.scrollbar = scrollbar
    }
  }

  const renderShopButton = () => {
    return window.Ecwid && (
      <>
        <DelayLink
          className={classNames({
            [classes.menuButton]: true,
            [classes.menuButtonShop]: true,
            [classes.hide]: isNavOpen || (isMenuButtonHover && mediaQueryKey > 1),
          })}
          to={strings.shop_menu_link}
        >
          <SimpleSplitText
            text={strings.shop_menu_label}
            animation="linear"
          />
        </DelayLink>
        <CartButton
          className={classNames({
            [classes.menuButton]: true,
            [classes.icon]: true,
            [classes.hide]: isNavOpen || (isMenuButtonHover && mediaQueryKey > 1),
          })}
          onClick={handleCartClick}
          link={strings.shop_menu_link}
        />
      </>
    )
  }

  const handleMouseMainButton = (e) => {
    if (e.type === 'mouseenter') {
      setMenuButtonHover(true)
    }
    if (e.type === 'mouseleave') {
      setMenuButtonHover(false)
    }
  }

  const renderMainButton = () => {
    return (
      <button
        className={classNames({
          [classes.menuButton]: true,
          [classes.mainMenuButton]: true,
        })}
        onClick={handleMainMenuClick}
        aria-label="toggle-main-menu"
        ref={preventPressSpacebar}
        onMouseEnter={handleMouseMainButton}
        onMouseLeave={handleMouseMainButton}
      >
        <SimpleSplitText
          text={menuLabel}
          animation="linear"
          disableHover={isNavOpen}
        />
        <span
          className={classNames({
            [classes.closeLabel]: true,
            [classes.hide]: !isNavOpen,
          })}
          dangerouslySetInnerHTML={{ __html: closeLabel }}
        />
      </button>
    )
  }

  return (
    <header
      className={classNames({
        [classes.root]: true,
        [classes.whiteBackground]: isWhiteBackground || isNavOpen,
      })}
      ref={$root}
    >
      <div className={classes.container}>
        <div className={classes.wrapper}>
          <div
            className={classes.logo}
          >
            <Logo />
          </div>
          <div className={classes.nav}>
            {renderBackButton()}
            {renderCollectionButton()}
            {renderShopButton()}
            {renderMainButton()}
          </div>
        </div>
      </div>
    </header>
  )
}

Header.defaultProps = {
  treshold: 500,
  menuLabel: 'Menu',
  closeLabel: 'Close',
  socialNav: [],
}

export default Header
